/**
 * User HTML shown in the app: video descriptions (RichTextField, or plain text for older
 * videos) and comments (RichCommentEditor). The backend cleans it on save too.
 *
 * Only the formatting the editors produce is kept: paragraphs, line breaks, bold, italic,
 * underline, strike, lists and web/mail links (+ quotes, code, headings and https images in
 * comments). Every other attribute is dropped, and script-like elements are removed with
 * their content.
 */
const ALLOWED_TAGS = new Set(['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'a']);
const COMMENT_TAGS = new Set([...ALLOWED_TAGS, 'blockquote', 'pre', 'code', 'h1', 'h2', 'h3', 'img']);
const DROPPED_WITH_CONTENT = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript', 'svg', 'math', 'head', 'title',
]);
const SAFE_HREF = /^(https?:|mailto:)/i;

/** True when the value is HTML from the editor (as opposed to an older plain-text description). */
export const isRichText = (value: string | null | undefined): boolean =>
  !!value && /<\/?(p|br|strong|b|em|i|u|s|ul|ol|li|a)(\s[^>]*)?\/?>/i.test(value);

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function sanitizeRichText(html: string, { comment = false }: { comment?: boolean } = {}): string {
  const allowed = comment ? COMMENT_TAGS : ALLOWED_TAGS;
  if (!html) return '';
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return escapeHtml(html);
  }
  const clean = (parent: Element) => {
    Array.from(parent.children).forEach((child) => {
      const tag = child.tagName.toLowerCase();
      if (DROPPED_WITH_CONTENT.has(tag)) {
        child.remove();
        return;
      }
      clean(child);
      if (!allowed.has(tag)) {
        child.replaceWith(...Array.from(child.childNodes));
        return;
      }
      const href = tag === 'a' ? (child.getAttribute('href') || '').trim() : '';
      const src = tag === 'img' ? (child.getAttribute('src') || '').trim() : '';
      const alt = tag === 'img' ? child.getAttribute('alt') || '' : '';
      Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name));
      if (tag === 'img') {
        if (/^https:\/\//i.test(src)) {
          child.setAttribute('src', src);
          if (alt) child.setAttribute('alt', alt);
        } else {
          child.remove();
        }
        return;
      }
      if (tag === 'a') {
        if (SAFE_HREF.test(href)) {
          child.setAttribute('href', href);
          child.setAttribute('target', '_blank');
          child.setAttribute('rel', 'noopener noreferrer nofollow');
        } else {
          child.replaceWith(...Array.from(child.childNodes));
        }
      }
    });
  };
  clean(doc.body);
  return doc.body.innerHTML;
}

/** Text only (tiles, previews, character counts). Blocks and line breaks become spaces. */
export function richTextToPlain(value: string | null | undefined): string {
  if (!value) return '';
  if (!isRichText(value)) return value;
  try {
    const doc = new DOMParser().parseFromString(
      value.replace(/<br\s*\/?>/gi, ' ').replace(/<\/(p|li)>/gi, ' </$1>'),
      'text/html',
    );
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  } catch {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}
